import * as React from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  return 'An unexpected error occurred.'
}

function errorStack(error: unknown): string | null {
  return error instanceof Error ? (error.stack ?? null) : null
}

/**
 * Shown in place of whatever crashed. Surfaces the real message rather than a
 * blank page — this is an internal staff tool, so the person hitting it is the
 * person who reports it, and a production build otherwise gives them nothing
 * but a minified error code.
 */
export function ErrorFallback({
  error,
  reset,
}: {
  error: unknown
  reset?: () => void
}) {
  const stack = errorStack(error)

  return (
    <div className="flex w-full items-center justify-center p-6">
      <Alert variant="destructive" className="max-w-2xl">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Something went wrong</AlertTitle>
        <AlertDescription className="space-y-3">
          <p className="font-mono text-xs break-words">{errorMessage(error)}</p>
          {stack && (
            <details className="text-xs">
              <summary className="cursor-pointer select-none text-muted-foreground">
                Technical details
              </summary>
              <pre className="mt-2 max-h-64 overflow-auto rounded bg-muted/50 p-2 text-[11px] leading-relaxed whitespace-pre-wrap">
                {stack}
              </pre>
            </details>
          )}
          <div className="flex gap-2 pt-1">
            {reset && (
              <Button size="sm" variant="outline" onClick={reset}>
                <RefreshCw className="mr-2 h-3.5 w-3.5" />
                Try again
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.location.reload()}
            >
              Reload page
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  )
}

/**
 * Last-resort boundary around the provider tree, for throws that happen
 * outside routing (providers, the root component). Route-level errors are
 * handled by the router's `defaultErrorComponent`, which is preferable because
 * it keeps the shell mounted and clears itself on navigation.
 *
 * A class component because React has no hook equivalent for componentDidCatch.
 */
export class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: unknown | null }
> {
  state: { error: unknown | null } = { error: null }

  static getDerivedStateFromError(error: unknown) {
    return { error }
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    console.error('Unhandled render error:', error, info.componentStack)
  }

  render() {
    if (this.state.error !== null) {
      return (
        <ErrorFallback
          error={this.state.error}
          reset={() => this.setState({ error: null })}
        />
      )
    }
    return this.props.children
  }
}
